// Migrated 1:1 from @nexato/nx-core-module (lib/pipes/relativeTimePipe.ts), #672.
// Transitive dependency of RelativeTimeComponent (app-relative-time): the component's
// template uses the `formatRelativeTime` pipe, which Angular resolves at build time
// (NO_ERRORS_SCHEMA does not suppress missing pipes). Migrated alongside the component
// so the local implementation builds without the library.
import { Pipe, PipeTransform } from '@angular/core';
import moment from 'moment';

/**
 * RelativeTimePipe
 *
 * This pipe transforms date strings into human-readable relative time formats.
 * Useful for displaying timestamps in notifications, social media posts, comments,
 * messaging apps, and any context where relative time is more meaningful than absolute dates.
 *
 * Usage: {{ dateString | formatRelativeTime }}
 */
@Pipe({
  name: 'formatRelativeTime',
})
export class RelativeTimePipe implements PipeTransform {
  /**
   * Transforms a date string into a human-readable relative time format.
   *
   * @param value - The date string to transform (any format that moment.js can parse)
   * @returns A formatted string representing the relative time
   */
  transform(value: string): string {
    // Get the current time
    const now = moment();

    // Parse the input date string
    const created = moment(value);

    // Calculate the difference in days
    const diff = now.diff(created, 'days');

    // If less than 1 day ago
    if (diff < 1) {
      // If less than 1 minute ago
      if (now.diff(created, 'minutes') < 1) {
        return 'gerade eben';
      }

      // If less than 1 hour ago
      if (now.diff(created, 'hours') < 1) {
        const minutes = now.diff(created, 'minutes');
        return `vor ${minutes} ${minutes === 1 ? 'Minute' : 'Minuten'}`;
      }

      // Return the difference in hours
      const hours = now.diff(created, 'hours');
      return `vor ${hours} ${hours === 1 ? 'Stunde' : 'Stunden'}`;
    }
    // If exactly 1 day ago
    else if (diff === 1) {
      return 'gestern';
    }
    // If less than 7 days ago
    else if (diff < 7) {
      return `vor ${diff} ${diff === 1 ? 'Tag' : 'Tagen'}`;
    }
    // If 7 or more days ago
    else {
      // Return a formatted date (e.g., "Apr 2, 2025")
      return "am " + created.format('MMM D, YYYY');
    }
  }
}
